import { FC } from "react";
import { Card, CardBody } from "@chakra-ui/react";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { DashboardResponse } from "../../api/models/DashboardResponse";
import { Shimmer } from "@am-tax/fe-core";
import { WidgetHeading } from "./WidgetHeading";

type ApplicationStatusWidgetProps = {
    isLoading: boolean;
    submissions: DashboardResponse["submissions"] | undefined;
};

const STATUS_ENTRIES: Array<{ label: string; key: keyof DashboardResponse["submissions"]; color: string }> = [
    { label: "Apps Submitted", key: "appsSubmitted", color: "var(--chakra-colors-blue-800)" },
    { label: "Certifications Pending", key: "certificationsPending", color: "var(--chakra-colors-cyan-500)" },
    { label: "Certifications Received", key: "certificationsReceived", color: "var(--chakra-colors-green-500)" },
    { label: "Appeal Pending", key: "appealPending", color: "var(--chakra-colors-purple-500)" },
    { label: "Appeal Approved", key: "appealApproved", color: "var(--chakra-colors-teal-400)" },
    { label: "Appeal Denied", key: "appealDenied", color: "var(--chakra-colors-orange-500)" },
];

export const ApplicationStatusWidget: FC<ApplicationStatusWidgetProps> = ({ isLoading, submissions }) => {
    const data = STATUS_ENTRIES.map(({ label, key }) => ({
        name: label,
        value: submissions?.[key] ?? 0,
    }));

    return (
        <Card flexGrow={1} overflow={"hidden"} variant={"withShadow"} width={"100%"} height={"100%"}>
            <CardBody display={"flex"} flexDirection={"column"} px={"1.25rem"} pt={"1rem"}>
                <WidgetHeading title="Application Status (YTD)" url="" />
                <Shimmer isLoading={isLoading} width="100%" height={320}>
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 48, left: 8, bottom: 4 }} barSize={20}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                            <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 12 }} />
                            <Tooltip cursor={{ fill: "var(--chakra-colors-gray-50)" }} />
                            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                {STATUS_ENTRIES.map(({ color }, i) => (
                                    <Cell key={i} fill={color} />
                                ))}
                                <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 600, fill: "#555" }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Shimmer>
            </CardBody>
        </Card>
    );
};
